<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import { ref } from 'vue';
import { ServiceCompanyAuditUsersApi, type ServiceCompanyAuditUser } from '@/api/service-company-audit-users';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import ServiceCompanyAuditUserForm from './service-company-audit-user-form.vue';
import ServiceCompanyAuditUserDelete from './service-company-audit-user-delete.vue';

interface Props {
  serviceCompanyId: number;
}

const props = defineProps<Props>();

const { tableProps } = useResponsiveTable();

const view = ref('list');
const selectedAuditUser = ref<ServiceCompanyAuditUser | null>(null);

const {
  data: auditUsers,
  refetch: refetchAuditUsers,
  isPending: isLoading,
} = useQuery({
  queryKey: ['serviceCompanyAuditUsers', props.serviceCompanyId],
  queryFn: () => ServiceCompanyAuditUsersApi.index(props.serviceCompanyId),
  initialData: [],
});

const actionSuccess = ref(false);

function openCreateForm() {
  selectedAuditUser.value = null;
  view.value = 'create';
}

function openUpdateForm(auditUser: ServiceCompanyAuditUser) {
  selectedAuditUser.value = auditUser;
  view.value = 'update';
}

function handleFormSuccess() {
  actionSuccess.value = true;
  refetchAuditUsers();
  view.value = 'list';
}

function handleFormClose() {
  view.value = 'list';
  selectedAuditUser.value = null;
}

function openDeleteForm(auditUser: ServiceCompanyAuditUser) {
  selectedAuditUser.value = auditUser;
  view.value = 'delete';
}

function handleDeleteSuccess() {
  refetchAuditUsers();
  actionSuccess.value = true;
  view.value = 'list';
  selectedAuditUser.value = null;
}

function handleDeleteCancel() {
  view.value = 'list';
  selectedAuditUser.value = null;
}

const headers = [
  { title: 'Email', key: 'email' },
  { title: 'Creado', key: 'humanCreatedAt' },
  { title: 'Acciones', key: 'actions', sortable: false },
];
</script>

<template>
  <v-card
    v-if="view === 'list'"
  >
    <v-card>
      <v-card-title class="mt-2 d-flex justify-space-between font-weight-bold">
        Usuarios de Auditoría
        <v-btn
          class="text-white border bg-primary"
          @click="openCreateForm"
        >
          <v-icon
            start
            icon="mdi-plus"
          />
          Agregar Usuario
        </v-btn>
      </v-card-title>

      <v-data-table
        v-bind="tableProps"
        :headers="headers"
        :header-props="{ class: 'font-weight-bold' }"
        :items="auditUsers"
        :loading="isLoading"
        loading-text="Cargando usuarios de auditoría..."
      >
        <template #[`item.actions`]="{ item }">
          <v-btn
            icon="mdi-pencil"
            size="small"
            variant="text"
            @click="openUpdateForm(item)"
          />
          <v-btn
            icon="mdi-delete"
            size="small"
            variant="text"
            color="error"
            @click="openDeleteForm(item)"
          />
        </template>
      </v-data-table>

      <v-snackbar
        v-model="actionSuccess"
        :timeout="2000"
        color="success"
        rounded="pill"
      >
        <v-icon
          icon="mdi-check-circle"
          class="mr-4"
        />
        ¡Operación completada exitosamente!
      </v-snackbar>
    </v-card>
  </v-card>

  <service-company-audit-user-form
    v-if="view === 'create'"
    :service-company-id="serviceCompanyId"
    :audit-user="null"
    @close="handleFormClose"
    @success="handleFormSuccess"
  />

  <service-company-audit-user-form
    v-if="view === 'update'"
    :service-company-id="serviceCompanyId"
    :audit-user="selectedAuditUser"
    @close="handleFormClose"
    @success="handleFormSuccess"
  />

  <service-company-audit-user-delete
    v-if="view === 'delete'"
    :audit-user="selectedAuditUser!"
    :service-company-id="serviceCompanyId"
    @success="handleDeleteSuccess"
    @cancel="handleDeleteCancel"
  />
</template>
